import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Modal, Button, Input } from '@/components/ui'
import { useRequestRefund } from '@/features/security-ledger/hooks/useSecurityLedger'
import {
  makeRefundRequestSchema,
  type RefundRequestFormValues,
} from '@/features/security-ledger/schemas/security-ledger.schemas'
import { formatLedgerAmount } from '@/features/security-ledger/lib/format'
import { BalancePreview } from './BalancePreview'

interface RefundRequestModalProps {
  companyId: string
  /** User document `_id` (Mongo ObjectId); used in refund URL segment (DTO name `employeeId` on backend). */
  targetUserId: string
  currentBalance: number
  currency: string
  open: boolean
  onClose: () => void
}

export function RefundRequestModal({
  companyId,
  targetUserId,
  currentBalance,
  currency,
  open,
  onClose,
}: RefundRequestModalProps): React.ReactElement | null {
  const mutation = useRequestRefund()
  const schema = useMemo(() => makeRefundRequestSchema(currentBalance), [currentBalance])

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<RefundRequestFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { amount: undefined, note: '' },
  })

  const amount = watch('amount')

  const onSubmit = (values: RefundRequestFormValues) => {
    mutation.mutate(
      { companyId, employeeId: targetUserId, body: { amount: values.amount, note: values.note || undefined } },
      { onSuccess: () => { reset(); onClose() } },
    )
  }

  const handleClose = () => { reset(); onClose() }

  if (!open) return null

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Request security deposit refund"
      footer={
        <div className="flex gap-2">
          <Button type="submit" form="refund-form" variant="primary" loading={mutation.isPending}>
            {mutation.isPending ? 'Submitting…' : 'Submit request'}
          </Button>
          <Button type="button" variant="secondary" onClick={handleClose} disabled={mutation.isPending}>
            Cancel
          </Button>
        </div>
      }
    >
      <form id="refund-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Current balance:{' '}
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {formatLedgerAmount(currentBalance, currency)}
            </span>
          </p>
          <button
            type="button"
            onClick={() =>
              setValue('amount', currentBalance, { shouldValidate: true, shouldDirty: true })
            }
            disabled={currentBalance <= 0}
            className="text-xs font-medium text-primary hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline"
          >
            Refund full balance
          </button>
        </div>
        <Input
          {...register('amount', { valueAsNumber: true })}
          id="refund-amount"
          label="Refund amount"
          type="number"
          step="0.01"
          min="0.01"
          max={currentBalance}
          required
          placeholder="0.00"
          error={errors.amount?.message}
        />
        <BalancePreview
          currentBalance={currentBalance}
          delta={amount}
          direction="decrease"
          currency={currency}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Submitting creates a pending request; the balance only changes once an approver approves it.
        </p>
        <div>
          <label
            htmlFor="refund-note"
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Note
          </label>
          <textarea
            {...register('note')}
            id="refund-note"
            rows={3}
            placeholder="e.g. Exit settlement"
            className="box-border w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
          />
        </div>
      </form>
    </Modal>
  )
}

RefundRequestModal.displayName = 'RefundRequestModal'
